import React, { useEffect, useState } from 'react';
import { Download, FileText, Image as ImageIcon, Contact, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

const kind = (ct = '') => (ct.startsWith('image/') ? 'image' : ct.includes('vcard') ? 'vcard' : 'file');

const parseVcard = (text) => {
  const lines = text.replace(/\r\n[ \t]/g, '').split(/\r?\n/);
  const val = (l) => l.slice(l.indexOf(':') + 1).trim();
  return {
    name: val(lines.find((l) => /^FN[:;]/i.test(l)) || ''),
    phones: lines.filter((l) => /^(item\d+\.)?TEL[:;]/i.test(l)).map(val),
    emails: lines.filter((l) => /^(item\d+\.)?EMAIL[:;]/i.test(l)).map(val),
  };
};

const filename = (headers, fallback) => {
  const m = /filename="([^"]+)"/.exec(headers?.['content-disposition'] || '');
  return m ? m[1] : fallback;
};

export function MediaAttachment({ msgId, idx, contentType }) {
  const url = `/whatsapp/messages/${msgId}/media/${idx}`;
  const k = kind(contentType);
  const [preview, setPreview] = useState(null);
  const [card, setCard] = useState(null);

  useEffect(() => {
    let objectUrl;
    if (k === 'image') {
      api.get(url, { responseType: 'blob' }).then(({ data }) => { objectUrl = URL.createObjectURL(data); setPreview(objectUrl); }).catch(() => setPreview(false));
    } else if (k === 'vcard') {
      api.get(url, { responseType: 'text' }).then(({ data }) => setCard(parseVcard(String(data)))).catch(() => setCard(false));
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, k]);

  const download = async () => {
    try {
      const res = await api.get(url, { params: { download: true }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = filename(res.headers, `whatsapp-adjunto-${idx + 1}`);
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) {
      let msg = 'No se pudo descargar el adjunto';
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* respuesta sin JSON */ }
      toast.error(msg);
    }
  };
  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copiado'));
  const Icon = k === 'image' ? ImageIcon : k === 'vcard' ? Contact : FileText;

  return (
    <div className="mt-2 rounded-md border border-[hsl(var(--border))] bg-black/20 p-2 space-y-2" data-testid={`whatsapp-media-${msgId}-${idx}`}>
      {k === 'image' && preview && <img src={preview} alt="Imagen recibida" className="max-h-64 rounded" data-testid={`whatsapp-media-preview-${msgId}-${idx}`} />}
      {k === 'image' && preview === false && <div className="text-[11px] text-red-300">No se pudo cargar la vista previa</div>}
      {k === 'vcard' && card && (
        <div className="text-xs space-y-0.5" data-testid={`whatsapp-vcard-${msgId}-${idx}`}>
          <div className="font-semibold">{card.name || 'Contacto sin nombre'}</div>
          {[...card.phones, ...card.emails].map((v) => (
            <button key={v} type="button" onClick={() => copy(v)} className="flex items-center gap-1 hover:underline"><Copy className="h-3 w-3" />{v}</button>
          ))}
        </div>
      )}
      {k === 'vcard' && card === false && <div className="text-[11px] text-red-300">No se pudo leer la tarjeta de contacto</div>}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] flex items-center gap-1 text-[hsl(var(--muted-foreground))]">
          <Icon className="h-3.5 w-3.5" />{k === 'image' ? 'Imagen' : k === 'vcard' ? 'Tarjeta de contacto' : contentType || 'Archivo'}
        </span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={download} data-testid={`whatsapp-media-download-${msgId}-${idx}`}>
          <Download className="h-3.5 w-3.5 mr-1" />Descargar
        </Button>
      </div>
    </div>
  );
}
